import { isClientAccount, type User } from '../../../shared/models/user.model';
import type { BadgeVariant } from '../../../shared/ui';
import type {
  ProfileOrderStatusKey,
  ProfilePrepurchaseProofKey,
  ProfilePrepurchaseStateKey,
} from '../data/profile-orders.models';
import { PROFILE_COPY } from '../profile.copy';

/**
 * Variante del badge de estado (§5.6.3). La etiqueta la pinta el servidor (`status.label`); la clave solo elige el
 * color: anulado y vencido → `danger`; entregado y completado → `success`; el resto, en curso → `info`.
 */
const STATUS_VARIANTS: Record<ProfileOrderStatusKey, BadgeVariant> = {
  anulado: 'danger',
  apartado_vencido: 'danger',
  completado: 'success',
  entregado: 'success',
  listo: 'info',
  en_camino: 'info',
  preparando: 'info',
  confirmado: 'info',
  pendiente: 'info',
};

/** Una clave fuera del contrato solo puede venir de una divergencia: se pinta como «en curso». */
export function orderStatusVariant(key: string): BadgeVariant {
  return STATUS_VARIANTS[key as ProfileOrderStatusKey] ?? 'info';
}

/**
 * Variante del badge de estado de una precompra en curso (A2b). La etiqueta la pinta el servidor (`state.label`): recibida
 * → `info`, pendiente de pago → `warning`, esperando el producto → `pre-sale`, ya llegó → `success`.
 */
const PREPURCHASE_STATE_VARIANTS: Record<ProfilePrepurchaseStateKey, BadgeVariant> = {
  nuevo: 'info',
  pendiente: 'warning',
  esperando: 'pre-sale',
  avisar: 'success',
};

/** Una clave fuera del contrato (la API ya manda las desconocidas como `nuevo`) se pinta como `info`. */
export function prepurchaseStateVariant(key: string): BadgeVariant {
  return Object.hasOwn(PREPURCHASE_STATE_VARIANTS, key)
    ? PREPURCHASE_STATE_VARIANTS[key as ProfilePrepurchaseStateKey]
    : 'info';
}

/** Comprobante manual de la precompra: en revisión → `warning`, rechazado → `danger`, verificado → `success`. */
const PREPURCHASE_PROOF_VARIANTS: Record<ProfilePrepurchaseProofKey, BadgeVariant> = {
  pending: 'warning',
  rejected: 'danger',
  accepted: 'success',
};

/** Una clave fuera del contrato se pinta como `info`. */
export function prepurchaseProofVariant(key: string): BadgeVariant {
  return Object.hasOwn(PREPURCHASE_PROOF_VARIANTS, key)
    ? PREPURCHASE_PROOF_VARIANTS[key as ProfilePrepurchaseProofKey]
    : 'info';
}

/**
 * B44: una cuenta `cliente` con `verified === false` no ve pedidos (la API devuelve vacío y 404). La web no pide nada
 * y pinta el aviso. `undefined` no es «sin verificar»; las cuentas que no son `cliente` ven lo que mande la API.
 */
export function ordersBlockedByVerification(user: User | null): boolean {
  return isClientAccount(user) && user?.verified === false;
}

/** «1 artículo» / «3 artículos». */
export function itemsCountLabel(count: number): string {
  const template = count === 1 ? PROFILE_COPY.ORDERS.ITEMS_ONE : PROFILE_COPY.ORDERS.ITEMS_OTHER;
  return template.replace('{n}', String(count));
}
